import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Play, Pause, Clock, Youtube, Podcast, FileText } from "lucide-react";
import { fetchEpisode } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";
import { MaskedLine, Reveal } from "@/components/Reveal";
import Marquee from "@/components/Marquee";

export default function EpisodeDetail() {
    const { id } = useParams();
    const [ep, setEp] = useState(null);
    const [missing, setMissing] = useState(false);
    const { current, playing, playEpisode } = usePlayer();

    useEffect(() => {
        fetchEpisode(id)
            .then(setEp)
            .catch(() => setMissing(true));
    }, [id]);

    if (missing) {
        return (
            <div data-testid="episode-not-found" className="min-h-[60vh] flex flex-col items-start justify-center max-w-7xl mx-auto px-5 sm:px-8">
                <h1 className="font-display font-black text-4xl text-ink">That page fell off the local train.</h1>
                <Link to="/episodes" data-testid="episode-notfound-back" className="mt-6 inline-flex items-center gap-2 font-sub font-bold text-pink">
                    <ArrowLeft className="w-4 h-4" /> Back to the archive
                </Link>
            </div>
        );
    }

    if (!ep) {
        return (
            <div data-testid="episode-loading" className="min-h-[60vh] flex items-center justify-center">
                <p className="font-sub font-bold text-ink/60">Cueing up the episode…</p>
            </div>
        );
    }

    const isPlaying = current?.id === ep.id && playing;

    return (
        <div data-testid="episode-detail-page">
            <section className="bg-paper border-b-2 border-ink">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-12 pb-16 sm:pt-16 sm:pb-20">
                    <Link
                        to="/episodes"
                        data-testid="episode-back-link"
                        className="inline-flex items-center gap-1.5 font-sub font-bold text-sm text-ink/60 hover:text-pink transition-colors duration-150"
                    >
                        <ArrowLeft className="w-4 h-4" /> All episodes
                    </Link>
                    <div className="mt-8 grid lg:grid-cols-12 gap-12 items-start">
                        <div className="lg:col-span-7">
                            <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink">
                                Episode {ep.number} · {ep.published_at}
                            </p>
                            <h1
                                data-testid="episode-detail-title"
                                className="mt-4 font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-ink"
                            >
                                <MaskedLine delay={0.1}>{ep.title}</MaskedLine>
                            </h1>
                            <Reveal delay={0.25}>
                                <p className="mt-4 font-sub italic text-lg text-ink/70">{ep.subtitle}</p>
                                <div className="mt-6 flex flex-wrap items-center gap-3">
                                    <button
                                        data-testid="episode-detail-play-btn"
                                        onClick={() => playEpisode(ep)}
                                        className="inline-flex items-center gap-2.5 bg-pink text-cream font-sub font-bold px-7 py-4 border-2 border-ink hard-shadow transition-transform duration-200 hover:-translate-y-1"
                                    >
                                        {isPlaying ? (
                                            <Pause className="w-5 h-5" fill="currentColor" />
                                        ) : (
                                            <Play className="w-5 h-5" fill="currentColor" />
                                        )}
                                        {isPlaying ? "Pause" : "Play episode"}
                                    </button>
                                    <span className="inline-flex items-center gap-1.5 font-mono-brand text-xs text-ink border border-ink/30 px-3 py-2">
                                        <Clock className="w-3.5 h-3.5" /> {ep.duration}
                                    </span>
                                    {ep.tags.map((t) => (
                                        <span key={t} className="font-mono-brand text-xs uppercase tracking-wider bg-mustard/30 text-ink px-3 py-2 border border-ink/20">
                                            {t}
                                        </span>
                                    ))}
                                </div>
                                <div className="mt-6 flex items-center gap-5">
                                    <a href={ep.spotify_url} target="_blank" rel="noopener noreferrer" data-testid="episode-detail-spotify" className="inline-flex items-center gap-1.5 font-sub font-bold text-sm text-ink hover:text-pink transition-colors duration-150">
                                        <Podcast className="w-4 h-4" /> Spotify
                                    </a>
                                    <a href={ep.youtube_url} target="_blank" rel="noopener noreferrer" data-testid="episode-detail-youtube" className="inline-flex items-center gap-1.5 font-sub font-bold text-sm text-ink hover:text-pink transition-colors duration-150">
                                        <Youtube className="w-4 h-4" /> YouTube
                                    </a>
                                </div>
                            </Reveal>
                        </div>
                        <Reveal delay={0.2} className="lg:col-span-5">
                            <div className="relative rotate-1">
                                <img
                                    src={ep.poster_art}
                                    alt={`${ep.title} poster`}
                                    data-testid="episode-detail-poster"
                                    className="w-full border-2 border-ink hard-shadow-pink object-cover object-top aspect-[4/5]"
                                />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <Marquee />

            {/* Show notes + guest bio */}
            <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-7">
                    <Reveal>
                        <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-pink">Show notes</p>
                        <h2 className="mt-3 font-display font-black text-2xl sm:text-3xl text-ink tracking-tight">
                            What we get into
                        </h2>
                    </Reveal>
                    {ep.show_notes && ep.show_notes.length > 0 ? (
                        <ul className="mt-8 space-y-5" data-testid="episode-show-notes">
                            {ep.show_notes.map((note, i) => (
                                <Reveal key={i} delay={i * 0.07}>
                                    <li className="flex gap-4 items-start bg-cream border-2 border-ink hard-shadow-sm p-5 -rotate-[0.4deg]">
                                        <span className="font-mono-brand text-xs text-pink mt-1 shrink-0">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                        <p className="text-sm sm:text-base leading-relaxed text-ink/85">{note}</p>
                                    </li>
                                </Reveal>
                            ))}
                        </ul>
                    ) : (
                        <p className="mt-8 text-ink/60 font-sub">Notes for this one are on their way.</p>
                    )}
                </div>
                <div className="lg:col-span-5">
                    <Reveal delay={0.1}>
                        <div data-testid="episode-guest-card" className="bg-ink text-paper border-2 border-ink hard-shadow-pink p-7 rotate-1">
                            <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-mustard">
                                The voice
                            </p>
                            <h3 className="mt-3 font-display font-black text-3xl">{ep.guest}</h3>
                            <p className="mt-1 font-sub italic text-paper/70">{ep.guest_role}</p>
                            <p className="mt-5 text-sm leading-relaxed text-paper/85 border-l-4 border-pink pl-4">
                                {ep.guest_bio || ep.guest_quote}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Transcript */}
            <section className="max-w-4xl mx-auto px-5 sm:px-8 pb-24">
                <Reveal>
                    <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-pink" />
                        <h2 className="font-display font-black text-2xl sm:text-3xl text-ink tracking-tight">
                            Read the transcript
                        </h2>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    {ep.transcript ? (
                        <div
                            data-testid="episode-transcript"
                            className="mt-8 bg-cream border-2 border-ink hard-shadow-sm p-7 sm:p-9 text-sm sm:text-base leading-loose text-ink/85 whitespace-pre-wrap font-sub"
                        >
                            {ep.transcript}
                        </div>
                    ) : (
                        <div
                            data-testid="episode-transcript-empty"
                            className="mt-8 bg-paperalt border-2 border-dashed border-ink/40 p-9"
                        >
                            <p className="font-sub font-bold text-ink/60">
                                The full transcript is being typed up, check back soon. Or drop a
                                question on the home page and we'll nudge it up the list.
                            </p>
                        </div>
                    )}
                </Reveal>
            </section>
        </div>
    );
}
